import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Printer, Save, Loader2, Calculator, Camera, FolderOpen, Pencil, Clock } from "lucide-react";
import { BodyshopCopy, countLines, countParts } from "@/components/BodyshopCopy";
import { BodyshopLive } from "@/components/BodyshopLive";
import { compress } from "@/components/InspectionPhotos";
import { FitToWidth } from "@/components/FitToWidth";
import { MoreMenu } from "@/components/MoreMenu";
import { JobEditSheet } from "@/components/JobEditSheet";
import { HoursMaterialsSheet } from "@/components/HoursMaterialsSheet";
import { PairPrintDialog } from "@/components/PairPrintDialog";

const today = () => new Date().toLocaleDateString("sv-SE");
const EMPTY = { number: "", brand: "", model: "", plate: "", vin: "", color: "", km: "", customer_name: "", customer_phone: "",
  date_in: today(), date_out: "", works: "", parts: "", notes: "", quote_total: "" };

const Cut = () => (
  <div className="w-0 border-l-2 border-dashed border-neutral-400 relative">
    <span className="absolute top-1/2 -left-[5px] -translate-y-1/2 text-[9px] text-neutral-400 [writing-mode:vertical-rl]">✂ taglia qui</span>
  </div>
);

// Print-only A4 landscape: vehicle or office copy; "pair" puts a second job's vehicle copy on the other half
const PrintSheet = ({ f, mode, live, second }) => (
  <div id="bodyshop-sheet" className="hidden print:flex bg-white text-black w-[297mm] h-[210mm]" data-testid="bodyshop-print-sheet">
    {mode === "office" ? <BodyshopCopy f={f} office live={live} /> : <BodyshopCopy f={f} />}
    <Cut />
    {mode === "pair" && second ? <BodyshopCopy f={second} /> : <div className="w-1/2" />}
  </div>
);

const usePhotoReader = (onData) => {
  const cam = useRef(null);
  const gal = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await compress(file, 2560, 0.9));
      const { data } = await api.post("/admin/bodyshop/read-sheet", fd);
      const n = Object.keys(data).length;
      if (!n) toast.error("Nessun dato trovato nella foto"); else { onData(data); toast.success(`Compilati ${n} campi dalla foto: controlla e salva`); }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const inputs = (
    <>
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="bodyshop-read-camera-input" />
      <input ref={gal} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="bodyshop-read-upload-input" />
    </>
  );
  return { inputs, busy, camera: () => cam.current?.click(), gallery: () => gal.current?.click() };
};

export default function BodyshopJobPage() {
  const { id } = useParams();
  const isNew = id === "nuova";
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [f, setF] = useState(() => {
    if (!isNew) return null;
    const [brand = "", ...model] = (params.get("veicolo") || "").trim().split(/\s+/);
    return { ...EMPTY, plate: (params.get("targa") || "").toUpperCase(), brand, model: model.join(" "), date_in: params.get("dal") || EMPTY.date_in };
  });
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("car");
  const [live, setLive] = useState(null);
  const [editing, setEditing] = useState(() => params.get("modifica") === "1");
  const dirty = useRef(false);
  const [focusKey, setFocusKey] = useState(null);
  const [touch] = useState(() => window.matchMedia("(pointer: coarse)").matches);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const openHours = () => (isNew ? toast.info("Salva prima la scheda, poi potrai aggiungere ore e materiali") : setHoursOpen(true));
  const pick = (k) => {
    if (k === "_consuntivo") return !isNew && nav(`/admin/carrozzeria/${id}/consuntivo`);
    setFocusKey(k);
    setEditing(true);
  };
  const closeEdit = (v) => {
    setEditing(v);
    if (v) return;
    if (params.get("modifica")) setParams({}, { replace: true });
    if (dirty.current) toast.warning("Modifiche non ancora salvate: premi Salva", { id: "unsaved" });
  };
  const set = (k, v) => { dirty.current = true; setF((p) => ({ ...p, [k]: k === "plate" ? v.toUpperCase() : v })); };
  const reader = usePhotoReader((d) => setF((p) => ({ ...p, ...d })));

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/bodyshop/${id}`).then(({ data }) => setF({ ...EMPTY, ...data })).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [id, isNew]);

  useEffect(() => {
    const st = document.createElement("style");
    st.textContent = "@media print { @page { size: A4 landscape; margin: 0; } }";
    document.head.appendChild(st);
    return () => st.remove();
  }, []);

  const print = (m) => { setMode(m); setTimeout(() => window.print(), 250); };
  const [pairOpen, setPairOpen] = useState(false);
  const [second, setSecond] = useState(null);
  const pickPair = async (j) => {
    setPairOpen(false);
    try {
      const { data } = await api.get(`/admin/bodyshop/${j.id}`);
      setSecond({ ...EMPTY, ...data });
      run("pair");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const printed = useRef(false);
  useEffect(() => {
    if (!f || printed.current || params.get("stampa") !== "1") return;
    printed.current = true;
    setParams({}, { replace: true });
    print("car");
  }, [f, params, setParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? ""]));
    const { data } = isNew ? await api.post("/admin/bodyshop", body) : await api.put(`/admin/bodyshop/${id}`, body);
    setF({ ...EMPTY, ...data });
    return data;
  };
  const run = async (printMode) => {
    setBusy(true);
    try {
      const data = await save();
      dirty.current = false;
      toast.dismiss("unsaved");
      closeEdit(false);
      toast.success("Scheda salvata");
      if (isNew && !printMode) return nav("/admin?tab=bodyshop&v=jobs");
      if (isNew) nav(`/admin/carrozzeria/${data.id}`, { replace: true });
      if (printMode) print(printMode);
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!f) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E63946]" /></div>;

  const menu = [
    { label: "Compila da foto (fotocamera)", icon: Camera, onClick: reader.camera, testid: "bodyshop-read-camera-btn" },
    { label: "Compila da foto (galleria)", icon: FolderOpen, onClick: reader.gallery, testid: "bodyshop-read-upload-btn" },
    null,
    { label: "Stampa copia veicolo", icon: Printer, onClick: () => run("car"), testid: "bodyshop-print-btn" },
    { label: "Stampa copia ufficio", icon: Printer, onClick: () => run("office"), testid: "bodyshop-print-office-btn" },
    { label: "Stampa 2 schede in un foglio", icon: Printer, onClick: () => setPairOpen(true), testid: "bodyshop-print-pair-btn" },
    !isNew && { label: "Correggi ore e materiali", icon: Clock, onClick: openHours, testid: "bodyshop-hours-btn" },
    !isNew && { label: "Consuntivo", icon: Calculator, to: `/admin/carrozzeria/${id}/consuntivo`, testid: "bodyshop-consuntivo-link" },
  ];
  const saveLabel = busy || reader.busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />;

  return (
    <div className="min-h-screen bg-[#0B0C10]">
      {reader.inputs}
      <header className="no-print sticky top-0 z-30 bg-[#12141A]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button asChild variant="outline" size="sm" className="rounded-full border-white/15 bg-transparent" data-testid="bodyshop-back-btn">
            <Link to="/admin?tab=bodyshop"><ArrowLeft className="w-4 h-4 mr-1.5" />Indietro</Link>
          </Button>
          <div className="font-display font-bold uppercase text-sm mr-auto truncate">{isNew ? "Nuova scheda" : `Scheda ${f.number || ""} ${f.plate || ""}`}</div>
          {reader.busy && <span className="text-xs text-[#3B82F6] flex items-center" data-testid="bodyshop-reading"><Loader2 className="w-4 h-4 animate-spin mr-1" />Lettura foto…</span>}
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="hidden sm:inline-flex rounded-full border-white/15 bg-transparent" data-testid="bodyshop-edit-btn"><Pencil className="w-4 h-4 mr-1.5" />Modifica</Button>
          <Button size="sm" onClick={() => run(null)} disabled={busy} className="hidden sm:inline-flex rounded-full bg-[#E63946] hover:bg-[#D62828] text-white px-5" data-testid="bodyshop-save-btn">{saveLabel}Salva</Button>
          <MoreMenu items={menu} testid="bodyshop-more" />
        </div>
      </header>

      <div className="no-print sm:hidden fixed bottom-0 inset-x-0 z-40 bg-[#12141A]/95 backdrop-blur border-t border-white/10 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]" data-testid="bodyshop-mobile-bar">
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditing(true)} className="flex-1 h-12 rounded-full border-white/15 bg-transparent text-base" data-testid="bodyshop-mobile-edit-btn"><Pencil className="w-4 h-4 mr-1.5" />Modifica</Button>
          <Button onClick={() => run(null)} disabled={busy} className="flex-1 h-12 rounded-full bg-[#E63946] hover:bg-[#D62828] text-white text-base" data-testid="bodyshop-mobile-save-btn">{saveLabel}Salva</Button>
        </div>
      </div>

      <div className="no-print max-w-7xl mx-auto px-4 py-5 pb-24 sm:pb-5 grid xl:grid-cols-[auto_1fr] gap-6 items-start">
        <div className="min-w-0">
          {isNew && !f.brand && !f.customer_name && (
            <button onClick={reader.camera} disabled={reader.busy} className="mb-3 w-full max-w-[148.5mm] flex items-center gap-3 rounded-xl border border-dashed border-[#3B82F6]/50 bg-[#3B82F6]/10 p-3 text-left text-sm hover:bg-[#3B82F6]/15 transition-colors" data-testid="bodyshop-photo-hint">
              <Camera className="w-5 h-5 text-[#3B82F6] shrink-0" /><span>Hai la scheda di carta? <b>Fotografala</b> e la compilo io. Altrimenti scrivi direttamente sul foglio.</span>
            </button>
          )}
          <p className="text-xs text-muted-foreground mb-2 max-w-[148.5mm]" data-testid="bodyshop-inline-hint">{touch ? <>Tocca un campo del foglio per correggerlo, anche <b>ore e materiali</b> a destra.</> : <>Scrivi direttamente sul foglio (Invio = riga successiva). Clicca la colonna <b>Operatori · ore</b> per correggere ore e materiali.</>} Le stampe sono nel menu <b>⋯</b>.</p>
          {(countLines(f) > 36 || countParts(f) > 9) && <p className="text-xs text-amber-400 mb-2 max-w-[148.5mm]" data-testid="bodyshop-too-many-lines">Molte righe ({countLines(f)}): sulla stampa alcune potrebbero non entrare.</p>}
          <FitToWidth width={148.5} height={210}>
            <div className="bg-white text-black w-[148.5mm] h-[210mm] flex shadow-2xl" data-testid="bodyshop-sheet">
              <div className="w-full h-full flex [&>div]:w-full"><BodyshopCopy f={f} set={set} office live={live} onPick={touch ? pick : undefined} onHours={openHours} /></div>
            </div>
          </FitToWidth>
        </div>
        <div className="min-w-0 xl:sticky xl:top-36">
          {isNew ? <div className="rounded-2xl border border-dashed border-white/15 p-6 text-sm text-muted-foreground" data-testid="bodyshop-live-empty">Salva la scheda per vedere ore lavorate e consuntivo in tempo reale.</div>
            : <BodyshopLive id={id} quote={f.quote_total} onData={setLive} reloadKey={reloadKey} />}
        </div>
      </div>
      <PrintSheet f={f} mode={mode} live={live} second={second} />
      <PairPrintDialog open={pairOpen} onOpenChange={setPairOpen} currentId={id} onPick={pickPair} />
      {!isNew && <HoursMaterialsSheet open={hoursOpen} onOpenChange={setHoursOpen} id={id} onSaved={() => setReloadKey((k) => k + 1)} />}
      <JobEditSheet open={editing} focusKey={focusKey} onOpenChange={(v) => { if (!v) setFocusKey(null); closeEdit(v); }} f={f} set={set} isNew={isNew} busy={busy} onSave={() => run(null)} onStatus={(status) => setF((p) => ({ ...p, status }))} />
    </div>
  );
}
